<script lang="ts">
  import { summarize } from '../lib/journey-summary';
  import { formatWalkMeters, walkLabel } from '../lib/leg-format';
  import { lineColor } from '../lib/mode-colors';
  import type { JourneyJson } from '../types/JourneyJson';
  import type { LegJson } from '../types/LegJson';
  import LineBadge from './line-badge.svelte';

  interface Properties {
    journey: JourneyJson;
    /** 1-based index for the aria-label ("Route 1: ..."). */
    index?: number;
    active: boolean;
    onSelect: () => void;
  }

  const { journey, index = 1, active, onSelect }: Properties = $props();

  const summary = $derived(summarize(journey));
  const timesLabel = $derived(`${summary.departure} → ${summary.arrival}`);
  const metaLabel = $derived(
    summary.transferLabel
      ? `${summary.durationLabel} · ${summary.transferLabel}`
      : summary.durationLabel,
  );
  const isEarliest = $derived(summary.isRecommended);
  const ariaLabel = $derived(buildAriaLabel(summary.departure, summary.arrival));

  function buildAriaLabel(departure: string, arrival: string): string {
    const bits = [
      `Route ${String(index)}`,
      `${departure} to ${arrival}`,
      summary.durationLabel,
      summary.transferLabel || 'direct',
    ];
    if (isEarliest) bits.push('earliest arrival');
    return bits.join(', ');
  }

  type Pill =
    | { key: string; kind: 'walk'; label: string }
    | { key: string; kind: 'transit'; label: string; color: string };

  function buildPills(legs: LegJson[]): Pill[] {
    return legs.map((journeyLeg, index_): Pill => {
      if (journeyLeg.type === 'walking') {
        const label = walkLabel(journeyLeg);
        const distance = formatWalkMeters(journeyLeg.walk_meters);
        return {
          key: `w${String(index_)}`,
          kind: 'walk',
          label: `${label} · ${distance}`,
        };
      }
      const label = journeyLeg.line.length > 0 ? journeyLeg.line : journeyLeg.mode;
      return {
        key: `t${String(index_)}-${label}`,
        kind: 'transit',
        label,
        color: lineColor(journeyLeg),
      };
    });
  }

  const pills = $derived(buildPills(journey.legs));
</script>

<!-- One journey result. Click flips selection so the map polyline + detail
     view can react. Recommended-earliest journey gets a green border + pill. -->
<button
  type="button"
  class="journey-card"
  class:active
  class:earliest={isEarliest}
  aria-pressed={active}
  aria-label={ariaLabel}
  onclick={onSelect}
>
  <div class="journey-times">
    <span class="times">{timesLabel}</span>
    <span class="meta">{metaLabel}</span>
    {#if isEarliest}
      <span class="badge-earliest">Earliest</span>
    {/if}
  </div>
  {#if pills.length > 0}
    <div class="journey-lines">
      {#each pills as pill (pill.key)}
        {#if pill.kind === 'walk'}
          <span class="badge-walk">{pill.label}</span>
        {:else}
          <LineBadge name={pill.label} color={pill.color} />
        {/if}
      {/each}
    </div>
  {/if}
</button>

<style>
  .journey-card {
    display: block;
    width: 100%;
    text-align: left;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    padding: var(--space-md);
    margin-bottom: var(--space-sm);
    cursor: pointer;
    color: var(--color-fg);
    font: inherit;
  }

  .journey-card:hover {
    border-color: var(--color-primary);
  }

  .journey-card.active {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgb(88 166 255 / 25%);
  }

  .journey-card.earliest {
    border-color: var(--color-accent-green);
    box-shadow: 0 0 0 2px rgb(63 185 80 / 25%);
  }

  .journey-card.earliest.active {
    box-shadow: 0 0 0 2px rgb(63 185 80 / 45%);
  }

  .journey-times {
    display: flex;
    align-items: baseline;
    gap: var(--space-sm);
    margin-bottom: var(--space-xs);
  }

  .times {
    font-weight: 600;
    font-size: var(--font-size-lg);
  }

  .meta {
    color: var(--color-fg-muted);
    font-size: var(--font-size-sm);
  }

  .badge-walk {
    background: var(--color-surface-alt);
    color: var(--color-fg-muted);
    padding: 1px 6px;
    border-radius: 3px;
    font-size: var(--font-size-xs);
    font-weight: 600;
    border: 1px dashed var(--color-border);
  }

  .badge-earliest {
    background: var(--color-accent-green);
    color: #000;
    padding: 1px 6px;
    border-radius: 3px;
    font-size: var(--font-size-xs);
    font-weight: 600;
    margin-left: auto;
  }

  .journey-lines {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-xs);
  }
</style>
